import { PRODUCT_TYPE_ENUM } from '@/utils/ProductTypeColors';

/**
 * Parser Constants
 * Centralized constants for the ParserV2 feature
 */

/**
 * Default column mapping structure used for Excel column selection
 * Maps required fields (EAN, PLU, NOM) to Excel columns
 */
export const DEFAULT_COLUMN_MAPPING = [
  { name: 'EAN', nameFile: '' },
  { name: 'PLU', nameFile: '' },
  { name: 'NOM', nameFile: '' }
];

/**
 * Nettoie un libellé produit venu du tableur client.
 *
 * Deux défauts observés sur les fichiers reçus, qui partaient jusqu'ici tels quels
 * dans codeMagasin.nom et produitLS.appellation, donc sur les écrans et les
 * étiquettes du magasin :
 *  - le nom scientifique collé derrière un « § » (« SEICHE PHARAON§Sepia pharaonis ») ;
 *  - des apostrophes redoublées par un export en amont (« L''''ATLANTIQUE »).
 *
 * Si le libellé commercial est vide une fois coupé, on garde la chaîne d'origine
 * nettoyée : un nom scientifique vaut mieux que pas de nom du tout.
 */
export const normalizeProductName = (name) => {
  const raw = String(name == null ? '' : name);
  const tidy = (value) => value.replace(/'{2,}/g, "'").replace(/\s+/g, ' ').trim();

  const parts = raw.split('§').map(tidy).filter(part => part !== '');
  return parts[0] || '';
};

/**
 * Reads a mapped field (EAN, PLU, NOM) from an Excel row.
 *
 * Le mapping saisi à l'étape 3 pointe vers les colonnes d'origine du fichier
 * (ex. NOM -> "Libellé"), mais le backend parser-v2-excel renvoie des lignes
 * normalisées sur les clés EAN/PLU/NOM. On tente donc la clé mappée, puis la
 * clé littérale, puis sa version minuscule.
 *
 * @param excelRow - Row object from the Excel data
 * @param field - Required field name ('EAN' | 'PLU' | 'NOM')
 * @param columnMapping - Array in step2 format [{name, nameFile}, ...]
 * @returns The value as a string, or '' if not found
 */
export const getExcelValue = (excelRow, field, columnMapping) => {
  if (!excelRow || !field) return '';

  // Le nettoyage passe par ici, seul point de lecture commun à l'affichage, à la
  // recherche, à la catégorisation et à l'écriture en base : sans ça, la table
  // montrerait un nom et le script en écrirait un autre.
  const read = (value) => field === 'NOM' ? normalizeProductName(value) : String(value);

  const mappedKey = columnMapping?.find(m => m.name === field)?.nameFile;
  if (mappedKey && excelRow[mappedKey] != null) return read(excelRow[mappedKey]);
  if (excelRow[field] != null) return read(excelRow[field]);

  const lowerKey = field.toLowerCase();
  if (excelRow[lowerKey] != null) return read(excelRow[lowerKey]);

  return '';
};

/**
 * Polling configuration for batch job processing
 */
export const POLLING_CONFIG = {
  INTERVAL_MS: 2000,      // Milliseconds between poll requests
  MAX_RETRIES: 300,       // Maximum number of retry attempts
  TIMEOUT_MS: 600000,     // Total timeout (10 minutes)
  DEFAULT_COUNT: 10       // Default number of results per request
};

/**
 * Job status constants
 */
export const JOB_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

/**
 * Column auto-selection patterns for intelligent column mapping
 * Each key corresponds to a required field, values are possible column name matches
 */
export const COLUMN_MAPPING_PATTERNS = {
  EAN: ['ean', 'eanb', 'code_ean', 'barcode', 'code_barre', 'codebarre', 'gtin'],
  PLU: ['plu', 'code_plu', 'codeplu', 'reference', 'ref', 'code'],
  NOM: ['nom', 'libelle', 'designation', 'name', 'produit', 'product', 'description']
};

/**
 * Auto-detects column mapping from Excel column names
 * @param excelColumnNames - Array of column names from the Excel file
 * @returns Object mapping field names (EAN, PLU, NOM) to Excel column names
 */
export const detectColumnMapping = (excelColumnNames) => {
  if (!Array.isArray(excelColumnNames) || excelColumnNames.length === 0) {
    return { EAN: '', PLU: '', NOM: '' };
  }

  const findMatchingColumn = (fieldName) => {
    const patterns = COLUMN_MAPPING_PATTERNS[fieldName] || [];
    const matchedColumn = excelColumnNames.find(columnName =>
      patterns.some(pattern =>
        columnName.toLowerCase().includes(pattern.toLowerCase())
      )
    );
    return matchedColumn || '';
  };

  return {
    EAN: findMatchingColumn('EAN'),
    PLU: findMatchingColumn('PLU'),
    NOM: findMatchingColumn('NOM')
  };
};

/**
 * Converts a column mapping object to step2 format
 * @param mapping - Object with EAN, PLU, NOM keys mapped to Excel column names
 * @returns Array in step2 format [{name, nameFile}, ...]
 */
export const mappingToStep2Format = (mapping) => {
  return [
    { name: 'EAN', nameFile: mapping.EAN || '' },
    { name: 'PLU', nameFile: mapping.PLU || '' },
    { name: 'NOM', nameFile: mapping.NOM || '' }
  ];
};

/**
 * Extracts column names from first Excel row
 * @param firstRow - First row object from Excel data
 * @returns Array of column names
 */
export const extractColumnNamesFromRow = (firstRow) => {
  if (!firstRow || typeof firstRow !== 'object') {
    return [];
  }
  return Object.keys(firstRow);
};

/**
 * Types de fichier PLU, alignés sur la colonne `type` posée par le formulaire
 * d'inscription (table `code` du service integration). Le parser s'en sert pour
 * pré-catégoriser les lignes du fichier analysé.
 */
export const PLU_FILE_TYPES = [
  { value: 'traditionnel', label: 'Traditionnels', icon: 'set_meal', color: 'blue-grey' },
  { value: 'recettes', label: 'Recettes', icon: 'soup_kitchen', color: 'deep-orange' },
  { value: 'libre-service', label: 'Libre-service', icon: 'inventory_2', color: 'indigo' }
];

export const DEFAULT_PLU_FILE_TYPE = 'traditionnel';

/**
 * Type de produit appliqué aux lignes d'un fichier, selon son type déclaré.
 *
 * Ce type prime sur la détection automatique (voir resolveProductType dans le
 * store) : un fichier libre-service ne contient que des produits emballés, dont
 * l'EAN à 13 chiffres ferait basculer chaque ligne en « Non-maison » si on
 * laissait la détection décider. Un fichier de traditionnels ne force rien : ses
 * lignes gardent le type déduit du parser.
 *
 * Un fichier libre-service part en « Produit LS » et non en « Industriel » : la
 * sortie en base est la même (produitLS + idProduitLS sur codeMagasin), mais la
 * composition reste éditable ligne par ligne, ce qu'un produit industriel perd.
 */
const FILE_TYPE_TO_PRODUCT_TYPE = {
  recettes: PRODUCT_TYPE_ENUM.MAISON,
  'libre-service': PRODUCT_TYPE_ENUM.PRODUIT_LS
};

export const defaultProductTypeForFile = (fileType) => FILE_TYPE_TO_PRODUCT_TYPE[fileType] || null;

export const pluFileTypeLabel = (fileType) => {
  const found = PLU_FILE_TYPES.find(type => type.value === fileType);
  return found ? found.label : null;
};

export const pluFileType = (fileType) => PLU_FILE_TYPES.find(type => type.value === fileType) || null;
